import { useState, useEffect } from 'react'
import { formatPhoneInput, toE164, isValidPhone } from '../../utils/formatPhone'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  CircularProgress,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material'
import { Product, Package, User, productsApi, packagesApi, usersApi, settingsApi, ApiError } from '../../services/api'
import { formatSalaryInput } from '../../utils/formatCurrency'

interface AddLeadDialogProps {
  open: boolean
  onClose: () => void
  onAdd: (lead: NewLeadData) => Promise<void>
}

export interface NewLeadData {
  name: string
  email: string
  phone: string
  source: string
  salaryTarget: string
  notes?: string
  selectedProducts?: string[]
  selectedPackageId?: string
  assignedTo?: string
}

const LEAD_SOURCES = [
  'LinkedIn',
  'Website',
  'Referral',
  'Instagram',
  'Facebook',
  'Cold Outreach',
  'Event',
  'Other',
]

export default function AddLeadDialog({ open, onClose, onAdd }: AddLeadDialogProps) {
  const [formData, setFormData] = useState<NewLeadData>({
    name: '',
    email: '',
    phone: '',
    source: '',
    salaryTarget: '',
    notes: '',
    selectedProducts: [],
    selectedPackageId: '',
    assignedTo: '',
  })

  const [errors, setErrors] = useState<Partial<Record<keyof NewLeadData, string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [products, setProducts] = useState<Product[]>([])
  const [packages, setPackages] = useState<Package[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loadingCatalog, setLoadingCatalog] = useState(false)

  useEffect(() => {
    if (open) {
      loadCatalog()
    }
  }, [open])

  const loadCatalog = async () => {
    try {
      setLoadingCatalog(true)
      const [productsData, packagesData, usersData, defaultOwnerSetting] = await Promise.all([
        productsApi.list(),
        packagesApi.list(),
        usersApi.list({ status: 'active' }),
        settingsApi.get('defaultLeadOwner').catch(() => ({ key: 'defaultLeadOwner', value: null })),
      ])
      setProducts(productsData || [])
      setPackages(packagesData || [])
      // Filter to only show users who can own leads (sales, managers, admins)
      const eligibleUsers = (usersData || []).filter((u) =>
        ['admin', 'manager', 'sales', 'client_success'].includes(u.role)
      )
      setUsers(eligibleUsers)

      // Set default owner if configured and user hasn't already selected one
      if (defaultOwnerSetting?.value && !formData.assignedTo) {
        setFormData((prev) => ({ ...prev, assignedTo: defaultOwnerSetting.value || '' }))
      }
    } catch (err) {
      console.error('Failed to load product catalog:', err)
    } finally {
      setLoadingCatalog(false)
    }
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price)
  }

  const handleChange = (field: keyof NewLeadData, value: string) => {
    setFormData({ ...formData, [field]: value })
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined })
    }
  }

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof NewLeadData, string>> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    if (!formData.source) {
      newErrors.source = 'Source is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setSubmitting(true)
    try {
      await onAdd({ ...formData, phone: formData.phone ? toE164(formData.phone) : '' })
      setFormData({
        name: '',
        email: '',
        phone: '',
        source: '',
        salaryTarget: '',
        notes: '',
        selectedProducts: [],
        selectedPackageId: '',
        assignedTo: '',
      })
      onClose()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.field) {
        setErrors((prev) => ({ ...prev, [err.field as keyof NewLeadData]: err.message }))
      }
      console.error('Failed to add lead:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      source: '',
      salaryTarget: '',
      notes: '',
      selectedProducts: [],
      selectedPackageId: '',
      assignedTo: '',
    })
    setErrors({})
    onClose()
  }

  const handleProductChange = (productIds: string[]) => {
    setFormData({ ...formData, selectedProducts: productIds, selectedPackageId: '' })
  }

  const handlePackageChange = (packageId: string) => {
    const selectedPkg = packages.find((p) => p.id === packageId)
    setFormData({
      ...formData,
      selectedPackageId: packageId,
      selectedProducts: selectedPkg?.productIds || [],
    })
  }

  const getSelectedProductNames = () => {
    return (formData.selectedProducts || [])
      .map((id) => products.find((p) => p.id === id)?.name || id)
      .join(', ')
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Lead</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            required
          />

          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            required
          />

          <TextField
            label="Phone"
            value={formatPhoneInput(formData.phone)}
            onChange={(e) => handleChange('phone', formatPhoneInput(e.target.value))}
            fullWidth
            error={!!(errors.phone || (formData.phone && !isValidPhone(formData.phone)))}
            helperText={errors.phone || (formData.phone && !isValidPhone(formData.phone) ? 'Enter a valid 10-digit phone number' : '')}
            placeholder="(555) 123-4567"
          />

          <TextField
            label="Source"
            select
            value={formData.source}
            onChange={(e) => handleChange('source', e.target.value)}
            error={!!errors.source}
            helperText={errors.source}
            fullWidth
            required
          >
            {LEAD_SOURCES.map((source) => (
              <MenuItem key={source} value={source}>
                {source}
              </MenuItem>
            ))}
          </TextField>

          <FormControl fullWidth disabled={loadingCatalog}>
            <InputLabel>Owner</InputLabel>
            <Select
              value={formData.assignedTo || ''}
              label="Owner"
              onChange={(e) => handleChange('assignedTo', e.target.value)}
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {users.map((user) => (
                <MenuItem key={user.email} value={user.email}>
                  {user.name} ({user.role})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Salary Target"
            value={formData.salaryTarget}
            onChange={(e) => handleChange('salaryTarget', formatSalaryInput(e.target.value))}
            placeholder="e.g., $100,000"
            fullWidth
          />

          <TextField
            label="Notes"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            multiline
            rows={3}
            fullWidth
          />

          {/* Product Selection Section */}
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Products / Services
          </Typography>

          {loadingCatalog ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {packages.length > 0 && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Select a Package</InputLabel>
                  <Select
                    value={formData.selectedPackageId || ''}
                    label="Select a Package"
                    onChange={(e) => handlePackageChange(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>No package (select individual products)</em>
                    </MenuItem>
                    {packages.map((pkg) => (
                      <MenuItem key={pkg.id} value={pkg.id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <span>
                            {pkg.name}
                            {pkg.isPopular && (
                              <Chip label="Popular" size="small" color="primary" sx={{ ml: 1, height: 18 }} />
                            )}
                          </span>
                          <Typography variant="body2" color="text.secondary">
                            {formatPrice(pkg.price)}
                            {pkg.discount && ` (${pkg.discount})`}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl fullWidth>
                <InputLabel>Individual Products</InputLabel>
                <Select
                  multiple
                  value={formData.selectedProducts || []}
                  onChange={(e) => handleProductChange(e.target.value as string[])}
                  input={<OutlinedInput label="Individual Products" />}
                  renderValue={() => getSelectedProductNames() || 'None selected'}
                  disabled={!!formData.selectedPackageId}
                >
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      <Checkbox checked={(formData.selectedProducts || []).includes(product.id)} />
                      <ListItemText
                        primary={product.name}
                        secondary={formatPrice(product.price)}
                      />
                    </MenuItem>
                  ))}
                </Select>
                {formData.selectedPackageId && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Products are set by the selected package
                  </Typography>
                )}
              </FormControl>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <CircularProgress size={24} color="inherit" /> : 'Add Lead'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
